import { useState } from 'react';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconCheckDefault, IconMoreHorizontal } from '@iwoca/icons';
import { clsx } from 'clsx';

import { useTrackWhatsNewViewDuration } from './hooks/useTrackWhatsNewViewDuration.hook';
import { WhatsNewCard } from './WhatsNewCard';
import {
  SellerWhatsNewNotification,
  useGetSellerWhatsNew,
  usePostSellerWhatsNewRead,
  usePostSellerWhatsNewReadAll,
} from '../../api/lending/lapiHooks';
import { Modal } from '../../components/Modal/Modal';
import { useSellerTrackingProperties } from '../../hooks/useSellerTrackingProperties';

const TabButton = ({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={isActive}
    onClick={onClick}
    className={clsx(
      'm-0 cursor-pointer whitespace-nowrap border-0 border-b-2 border-solid bg-transparent px-0 pb-s text-m font-bold',
      isActive
        ? 'border-black text-primary-10'
        : 'border-transparent text-secondary-40',
    )}
  >
    {label} ({count})
  </button>
);

// Inline text button on desktop; on mobile it collapses into a "⋯" overflow
// menu (per the design) so it doesn't crowd the tabs. Built on headless-ui Menu
// for focus management, Escape-to-close and keyboard nav — but rendered inline
// (NOT portalled), so it stays inside the modal's stacking context rather than
// portalling to document.body where it would sit behind the modal overlay.
const MarkAllAsRead = ({ onMarkAll }: { onMarkAll: () => void }) => (
  <>
    <button
      type="button"
      onClick={onMarkAll}
      className="m-0 hidden cursor-pointer items-center gap-2xs whitespace-nowrap border-0 bg-transparent p-0 text-s font-bold text-primary-40 min-l:flex"
    >
      <IconCheckDefault className="h-[16px] w-[16px]" />
      Mark all as read
    </button>

    <Menu as="div" className="relative min-l:hidden">
      <MenuButton
        aria-label="What's new options"
        className="m-0 flex cursor-pointer items-center justify-center border-0 bg-transparent p-2xs text-primary-10"
      >
        <IconMoreHorizontal className="h-[20px] w-[20px]" />
      </MenuButton>
      <MenuItems className="absolute right-0 top-full z-10 mt-2xs flex flex-col rounded-m border border-solid border-secondary-90 bg-structure-100 py-s shadow-blue-s">
        <MenuItem>
          <button
            type="button"
            onClick={onMarkAll}
            className="m-0 flex cursor-pointer items-center gap-s whitespace-nowrap border-0 bg-transparent px-m py-s text-s font-bold text-primary-10 data-[focus]:bg-structure-95"
          >
            <IconCheckDefault className="h-[16px] w-[16px]" />
            Mark all as read
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  </>
);

/**
 * Rendered only while the modal is open (ReactModal unmounts its children on
 * close), so this remounts fresh each open. It freezes which uuids were unread
 * the first time data is available, so marking-read-on-scroll doesn't reflow
 * cards under the user; the split regroups on the next open via the remount.
 */
const WhatsNewModalBody = () => {
  const { notifications, isLoading } = useGetSellerWhatsNew();
  const { markWhatsNewRead } = usePostSellerWhatsNewRead();
  const { markAllWhatsNewRead } = usePostSellerWhatsNewReadAll();
  const sellerTrackingProperties = useSellerTrackingProperties();

  const trackLearnMore = (notification: SellerWhatsNewNotification) =>
    tracking.ampli.whatsNewNotificationClicked({
      notification_uuid: notification.uuid,
      // Combine heading + subHeading into the single event field for now so
      // it's easier to recognise in Amplitude. Ideally these would be two
      // properties — may split later.
      notification_heading: [notification.heading, notification.subHeading]
        .filter(Boolean)
        .join(' — '),
      ...sellerTrackingProperties,
    });

  // "Adjust state during render" — React's endorsed way to capture the first
  // available value without an effect. Runs once, then never again.
  const [unreadOnOpen, setUnreadOnOpen] = useState<ReadonlySet<string> | null>(
    null,
  );
  if (unreadOnOpen === null && notifications.length > 0) {
    setUnreadOnOpen(
      new Set(
        notifications.filter(({ isRead }) => !isRead).map(({ uuid }) => uuid),
      ),
    );
  }

  const inUnreadSection = (notification: SellerWhatsNewNotification) =>
    unreadOnOpen === null
      ? !notification.isRead
      : unreadOnOpen.has(notification.uuid);

  const unread = notifications.filter(inUnreadSection);
  const read = notifications.filter((n) => !inUnreadSection(n));

  // Default to Unread, falling back to Read when nothing is unread. Once the
  // user picks a tab, activeTab sticks for the rest of the session.
  const [activeTab, setActiveTab] = useState<'unread' | 'read' | null>(null);
  const displayedTab = activeTab ?? (unread.length > 0 ? 'unread' : 'read');
  const shownNotifications = displayedTab === 'unread' ? unread : read;

  return (
    <div className="flex max-h-[min(80vh,634px)] flex-col gap-l overflow-y-auto border-0 border-t border-solid border-secondary-90 py-l pl-l pr-l [scrollbar-color:#d9d9d9_transparent] [scrollbar-width:thin] min-l:py-xl min-l:pl-xl min-l:pr-xl [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#d9d9d9] [&::-webkit-scrollbar]:w-[8px]">
      {isLoading ? (
        <p className="m-0 text-s text-secondary-40">Loading…</p>
      ) : unread.length === 0 && read.length === 0 ? (
        <p className="m-0 text-s text-secondary-40">
          You&apos;re all caught up!
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-m border-0 border-b border-solid border-black">
            <div className="flex gap-l min-l:gap-xl">
              <TabButton
                label="Unread"
                count={unread.length}
                isActive={displayedTab === 'unread'}
                onClick={() => setActiveTab('unread')}
              />
              <TabButton
                label="Read"
                count={read.length}
                isActive={displayedTab === 'read'}
                onClick={() => setActiveTab('read')}
              />
            </div>
            {/* Gated on the frozen unread split (not live read-state) so the
                action stays put for the session even as cards auto-mark read
                in view — it regroups on the next open. */}
            {unread.length > 0 && (
              <MarkAllAsRead
                onMarkAll={() =>
                  markAllWhatsNewRead(undefined, {
                    // Collapse the frozen unread split only once the server
                    // confirms. Doing it optimistically would, on a failed
                    // read_all, clear the counts and remove the button while the
                    // hook's onError toast tells the user to retry — a
                    // contradictory, unactionable state. On failure we leave the
                    // list and button in place so the retry is possible.
                    onSuccess: () => {
                      // Collapse the frozen split so the counts and button
                      // reflect the action (per-card dots already track live
                      // isRead).
                      setUnreadOnOpen(new Set());
                      // Pin the Unread tab so clearing it doesn't silently swap
                      // the user onto Read (displayedTab falls back to 'read'
                      // when nothing's unread) — they stay put and see the
                      // caught-up empty state.
                      setActiveTab('unread');
                    },
                  })
                }
              />
            )}
          </div>

          {shownNotifications.length === 0 ? (
            <p className="m-0 text-s text-secondary-40">
              {displayedTab === 'unread'
                ? "You're all caught up!"
                : 'Nothing here yet.'}
            </p>
          ) : (
            shownNotifications.map((notification) => (
              <WhatsNewCard
                key={notification.uuid}
                notification={notification}
                onView={() =>
                  displayedTab === 'unread'
                    ? markWhatsNewRead(notification.uuid)
                    : undefined
                }
                onLearnMore={() => trackLearnMore(notification)}
              />
            ))
          )}
        </>
      )}
    </div>
  );
};

export const WhatsNewModal = ({
  isOpen,
  closeModal,
}: {
  isOpen: boolean;
  closeModal: () => void;
}) => {
  useTrackWhatsNewViewDuration(isOpen);

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      isClosable={true}
      testPrefix="whatsNew"
      className="w-[calc(100vw-32px)] max-w-[750px] overflow-clip rounded-l border border-solid border-secondary-90 bg-structure-100 shadow-blue-s"
      overlayClassName="bg-black/65"
      disableContentPadding={true}
      header={
        <div className="bg-secondary-95 px-l py-xl min-l:px-xl">
          <h2 className="m-0 text-l font-bold text-primary-10 min-l:text-2xl">
            What&apos;s new
          </h2>
        </div>
      }
    >
      <WhatsNewModalBody />
    </Modal>
  );
};
